/**
 * Google AdSense slot. Renders only when the site has an `adsense_publisher_id`
 * and a slot id for the given placement. We inject the AdSense loader once and
 * push a new (adsbygoogle) for each slot mount.
 *
 * Placements: "header", "in_article", "sidebar", "footer"
 */
import React, { useEffect, useRef } from "react";

let loaderInjected = false;
function ensureLoader(pubId) {
  if (loaderInjected || !pubId) return;
  // Avoid duplicate injection across pages.
  if (document.querySelector(`script[data-ad-client="${pubId}"]`)) {
    loaderInjected = true;
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${pubId}`;
  s.setAttribute("data-ad-client", pubId);
  document.head.appendChild(s);
  loaderInjected = true;
}

const FORMAT_BY_PLACEMENT = {
  header: { format: "horizontal", layout: null },
  in_article: { format: "fluid", layout: "in-article" },
  sidebar: { format: "auto", layout: null },
  footer: { format: "horizontal", layout: null },
};

export default function AdSlot({ site, placement = "in_article", className = "" }) {
  const ref = useRef(null);
  const pushedRef = useRef(false);
  const pubId = site?.adsense_publisher_id;
  const slot = site?.adsense_slots?.[placement];
  const { format, layout } = FORMAT_BY_PLACEMENT[placement] || FORMAT_BY_PLACEMENT.in_article;

  useEffect(() => {
    if (!pubId || !slot) return;
    if (pushedRef.current) return; // prevent double-push under StrictMode / re-render
    // Also guard against the underlying <ins> already having been bound by AdSense
    const ins = ref.current?.querySelector("ins.adsbygoogle");
    if (ins && ins.getAttribute("data-adsbygoogle-status") === "done") {
      pushedRef.current = true;
      return;
    }
    ensureLoader(pubId);
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch (e) {
      // adsense will retry once it finishes loading
    }
  }, [pubId, slot, placement]);

  if (!pubId || !slot) {
    // No ad slot configured — render nothing (keeps templates clean for sites
    // not yet monetized).
    return null;
  }

  const insStyle = { display: "block", textAlign: "center" };
  const props = {
    className: "adsbygoogle",
    style: insStyle,
    "data-ad-client": pubId,
    "data-ad-slot": slot,
    "data-ad-format": format,
    "data-full-width-responsive": "true",
  };
  if (layout) props["data-ad-layout"] = layout;

  return (
    <div
      ref={ref}
      data-testid={`ad-slot-${placement}`}
      className={`ad-slot ad-slot-${placement} my-6 ${className}`}
      style={{ minHeight: placement === "in_article" ? 100 : 80 }}
    >
      <ins {...props}></ins>
    </div>
  );
}
